import { Form, Tooltip, Typography } from "antd";
import BaseModal from "components/BaseModal";
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { CUSTOM_FIELDS_WITH_SEARCH, CUSTOM_FIELDS_WITH_USER_API, FieldTypes, generateFormFields, getCustomFieldValue } from "utils/formUtils";
import _, { isArray, isEmpty, isNil, isString, isUndefined } from "lodash";
import { SEVERITY_NAMES } from "views/Findings/Constants";
import Vocabulary from "components/Vocabulary";
import message from "components/CustomMessage";
import { customParseSelectOptions, RenderCustomFields } from "../jira/JiraMappingForm";
import { MODAL_SIZE_LARGE, SEVERITY_COLOR } from "constants/Layout";
import { HelpIcon } from "components/Icons";
import { hasPermission } from "utils/authService";
import { PermissionSetMap } from "constants/RBACConstants";
import { getCustomFieldSelectedValuesPayload, getSeverityFieldMappingsPayload, integrationTicketingToolsProductOptionsMutator, ResolvedFindingStatusNames, useUnifiedMultiTemplateOptions, useUnifiedSingleTemplateOptions } from "../genericIntegrationTicketUtils";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { CONFIG_UPDATE_WARNING_MESSAGE } from "../genericIntegrationTicketConstants";
import { getEmptyCancellableRequest } from "restAPI";
import { validateBlankSpace } from "utils/validatorUtil";

const AZURE_BOARD_DEFAULT_VALUES = {
  product: "all",
  subProduct: ["all"],
  critical: "High",
  high: "High",
  medium: "Low",
  low: "Low",
  info: "Low",
};

const setAzureBoardCustomFields = (data) => {
  const customFields = [
    {
      key: "critical_ticket_severity_mapping",
      name: data.critical_ticket_severity_mapping,
    },
    {
      key: "high_ticket_severity_mapping",
      name: data.high_ticket_severity_mapping,
    },
    {
      key: "medium_ticket_severity_mapping",
      name: data.medium_ticket_severity_mapping,
    },
    {
      key: "low_ticket_severity_mapping",
      name: data.low_ticket_severity_mapping,
    },
    {
      key: "info_ticket_severity_mapping",
      name: data.info_ticket_severity_mapping,
    },
  ];

  return customFields;
};

function getInitialData(data = {}) {
  const { customFields } = data;
  _.forEach(customFields, (field) => {
    data[field.key] = getCustomFieldValue(field.type, field.defaultVal);
    if (field.key === "projectValue") {
      data.projectKey = field.value;
    }
  });
  data = {
    ...data,
    labels: data?.labels || ["Armorcode_Associated_Finding"],
    subProduct: !isEmpty(data?.subProductIds) ? data?.subProductIds : ["all"],
    product: !isEmpty(data?.product) ? data?.product : ["all"],
    ...data.properties,
  };
  const defaultValues = AZURE_BOARD_DEFAULT_VALUES;
  const d = { ...defaultValues };
  Object.keys(data).forEach((key) => {
    if (_.isPlainObject(data[key]) && data[key].id) {
      d[key] = data[key].id;
    } else if (data?.[key]) {
      d[key] = data?.[key];
    }
  });
  return d;
}

function AzureBoardMappingForm(
  {
    loginConfigId,
    onUpdateConfigComponent,
    name,
    displayName,
    url = undefined,
    ...props
  },
  ref
) {
  const [initialData] = useState(() => getInitialData(props.config));
  const isEdit = Boolean(props.config?.id);
  const [form] = Form.useForm();
  const [data, setData] = useState(initialData);
  const [isCustomFieldsLoading, setIsCustomFieldsLoading] = useState(false);
  const [priorities, setPriorities] = useState([]);
  const [transitionStatusOptions, setTransitionStatusOptions] = useState([]);
  const [azureBoardSeverities, setAzureBoardSeverities] = useState();
  const thisRef = useRef();
  const repoNamesRef = useRef();
  const [isResolvedStatusDisabled, setIsResolvedStatusDisabled] =  useState(!data.markResolved);
  const [markReopen, setMarkReopen] = useState(data.markReopen);
  const featureFlags = useFeatureFlag();
  const { getUnifiedSingleTemplates, unifiedSingleDefaultTemplate } =
  useUnifiedSingleTemplateOptions();
  const { getUnifiedMultiTemplates, unifiedMultiDefaultTemplate } =
  useUnifiedMultiTemplateOptions();
  useEffect(() => {
    if(typeof name !== 'string'){
        return;
    }
    if(!loginConfigId || !data.issueType || !data.projectKey){
        return;
    }
    
    const requestPayload = {
        loginConfigId,
        issueType: data.issueType,
        projectKey: data.projectKey,
    }
    const {promise, cancel } = ToolsIntegrationsAPI.getJiraCloseTransitionStatusOptions(requestPayload)
    promise.then(res => {
      setTransitionStatusOptions(res);
    });

    return cancel;
  },[loginConfigId,data.issueType,data.projectKey,name]);

  useEffect(() => {
    if(featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING){
      return;
    }
    if (data.projectKey) {
      setPriorities([]);
      const { cancel, promise } =
        ToolsIntegrationsAPI.getTicketSystemToolPriorities({
          projectKey: data.projectKey,
          loginConfigId,
        });
      promise.then((response) => {
        if (!(isEdit && data.projectKey === initialData.projectKey)) {
          const severities = {};
          const severityNames = Object.keys(SEVERITY_NAMES);
          response.forEach((entry, index) => {
            severities[severityNames[index]] = entry.id;
          });

          if (severityNames.length > response.length) {
            for (
              let index = response.length;
              index < severityNames.length;
              index++
            ) {
              severities[severityNames[index]] =
                severities[severityNames[index - 1]];
            }
          }
          form.setFieldsValue(severities);
        }
        setPriorities(response);
      });
      return cancel;
    }
  }, [isEdit, data.projectKey, loginConfigId, form, initialData.projectKey, featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING]);

  useEffect(() => {
    if(featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING){
      return;
    }
    if (data.projectKey && data.issueType) {
      setAzureBoardSeverities([]);
      const { cancel, promise } =
        ToolsIntegrationsAPI.getTicketSystemToolSeverities({
          projectKey: data.projectKey,
          loginConfigId,
          issueType: data.issueType
        });
      promise.then(setAzureBoardSeverities);
      return cancel;
    }
  }, [data.projectKey, loginConfigId, data.issueType, featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING]);

  const getProjectKeys = useCallback(() => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getTicketSystemToolProjects(loginConfigId);
    promise.then((res) => (repoNamesRef.current = res));
    return { promise, cancel };
  }, [loginConfigId]);

  const getIssueTypes = useCallback(() => {
    if (!data.projectKey) {
      return Promise.resolve([]);
    }
    return ToolsIntegrationsAPI.getTicketSystemToolIssueTypes({
      projectKey: data.projectKey,
      loginConfigId,
    });
  }, [data.projectKey, loginConfigId]);

  const fetchJiraLabels = useCallback((params) => {
    if(!loginConfigId){
      return getEmptyCancellableRequest([]);
    }
    return ToolsIntegrationsAPI.getJiraLabels({loginConfigId, ...params});
  }, [loginConfigId])

  const commonSeverityProps = useMemo(
    () => ({
      colSpan: 5,
      className: "m-b-0",
      type: FieldTypes.SELECT,
      style: { flex: "0 0 20%" },
      rules: [
        {
          required: true,
          message: `Select ${name} Priority`,
        },
      ],
      options: priorities,
      loading: data.projectKey && _.isEmpty(priorities),
      disabled: !data.projectKey,
    }),
    [data.projectKey, name, priorities]
  );

  const commonAzureBoardSeverityProps = useMemo(
    () => ({
      colSpan: 5,
      className: "m-b-0",
      type: FieldTypes.SELECT,
      style: { flex: "0 0 20%" },
      options: azureBoardSeverities,
      disabled: !data.projectKey || !data.issueType,
    }),
    [data.projectKey, azureBoardSeverities, data.issueType]
  );

  // if more than one product or All product is selected then subproduct should be disabled
  // this would work as previous, just will get the product id from array, as product is multi select
  const productId = data.product && data.product[0] !== "all" ? data.product[0] : null;

  const resetSubProductsField = useCallback((values) => { 
    setData(data => ({ ...data, product: values }));
    form.setFieldsValue({subProduct:["all"]});
  }, [form]);

  const appendAll = useCallback(
    (list) => [{ label: "All", value: "all" }, ...(list || [])],
    []
  );

  const onCreateSubproduct = useCallback((createdSubproduct) => {
    const subProductValues = form.getFieldValue("subProduct");
    form.setFieldsValue({
      subProduct: [...subProductValues, createdSubproduct.id],
    });
    setData((prevdata) => ({ ...prevdata, subProduct: [...subProductValues, createdSubproduct.id]}));
  }, [form]);

  const onCreateProduct = useCallback((createdProduct) => {
    const productsAfterCreate = [...form.getFieldValue("product"), createdProduct.id];
    form.setFieldsValue({
      product: productsAfterCreate,
      subProduct: ["all"],
    });
    setData((data) => ({ ...data, product: productsAfterCreate, subProduct: ["all"] }));
  }, [form]);

  const productSubProductFields = useMemo(
    () => [
      {
        name: "product",
        label: <Vocabulary name="product" />,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        required: true,
        colSpan: 12,
        // disabled:isEdit,
        props: {
          dropdownType: "product",
          showAddButton: true,
          optionsMutator: (options) => integrationTicketingToolsProductOptionsMutator(options, form.getFieldValue("product")),
          onChange: resetSubProductsField,
          onCreate: onCreateProduct,
          mode: "multiple"
        },
      },
      {
        name: "subProduct",
        label: <Vocabulary name="subProduct" />,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        required: true,
        colSpan: 12,
        disabled: data.product && data.product.length > 1,
        props: {
          productId: productId,
          showAddButton: Boolean(productId),
          dropdownType: "subProduct",
          onChange: (values) => {
            if (values.includes("all")) {
              setData((prevdata) => ({...prevdata, subProduct: ["all"]}));
              form.setFieldsValue({subProduct: ["all"]});
            } else {
              setData((prevdata) => ({ ...prevdata, subProduct: values }));
              form.setFieldsValue({subProduct: values});
            }
          },
          onCreate: onCreateSubproduct,
          optionsMutator: (options) => {
            const updatedOptions = appendAll(options);
            const selectedValues = form.getFieldValue("subProduct") || [];
            const isAllSelected = selectedValues.includes("all");
            return updatedOptions.map((option) => ({
              ...option,
              disabled: isAllSelected && option.value !== "all"
            }));
          },
          mode: "multiple"
        },
      },
    ],
    [appendAll, form, productId, resetSubProductsField, onCreateSubproduct, data.product, onCreateProduct]
  );

  const handleMarkResolveChecked = useCallback(event => {
    const checked = !data.projectKey || !data.issueType || !event.target.checked;
    setIsResolvedStatusDisabled(checked)
}, [data.projectKey, data.issueType]);

const handleMarkReopen = useCallback(event => {
  const checked = event.target.checked;
  setMarkReopen(checked)
}, []);

useEffect(() => {
  if (!isEdit) {
    form.setFieldsValue({
      ticketUnifiedSingleTemplateId: unifiedSingleDefaultTemplate,
      ticketUnifiedMultipleTemplateId: unifiedMultiDefaultTemplate,
    });
  }
}, [isEdit, form, unifiedSingleDefaultTemplate, unifiedMultiDefaultTemplate]);

  const fields = useMemo(
    () => [
      ...productSubProductFields,
      {
        name: "projectKey",
        label: "Project Key",
        colSpan: 12,
        type: FieldTypes.SELECT,
        required: true,
        props: {
          api: getProjectKeys,
          parseSelectOptions: customParseSelectOptions,
          onChange: (value) => {
            isEdit &&
            message.warn(CONFIG_UPDATE_WARNING_MESSAGE);
            form.setFieldsValue({
              "critical_ticket_severity_mapping": "",
              "high_ticket_severity_mapping": "",
              "medium_ticket_severity_mapping": "",
              "low_ticket_severity_mapping": "",
              "info_ticket_severity_mapping": ""
            });
            setData(prevData => ({ ...prevData, projectKey: value, issueType: null }));
            form.setFieldsValue({ issueType: null });
          },
        },
      },
      {
        name: "issueType",
        label: "Issue Type",
        colSpan: 12,
        type: FieldTypes.SELECT,
        required: true,
        disabled: !data.projectKey,
        props: {
          api: getIssueTypes,
          parseSelectOptions: customParseSelectOptions,
          onChange: (value) => {
            isEdit &&
            message.warn(CONFIG_UPDATE_WARNING_MESSAGE);
            const markResolved = form.getFieldValue('markResolved');
            form.setFieldsValue({
              "critical_ticket_severity_mapping": "",
              "high_ticket_severity_mapping": "",
              "medium_ticket_severity_mapping": "",
              "low_ticket_severity_mapping": "",
              "info_ticket_severity_mapping": ""
            });
            setData(prevData => ({...prevData,  issueType: value }));
            setIsResolvedStatusDisabled(!value || !markResolved);
          },
        },
      },
      {
        name: "defaultConfiguration",
        label: "Set as Default Project Key",
        inline: true,
        help: <>Use the Azure project as default for the given <Vocabulary name="product"/> and/or <Vocabulary name="subProduct"/> while creating tickets manually or through runbooks.</>,
        type: FieldTypes.BOOLEAN,
      },
      ...(featureFlags.UNIFIED_TICKET_TEMPLATE
        ? [
            {
              name: "ticketUnifiedSingleTemplateId",
              label: "Single Finding Template",
              colSpan: 12,
              type: FieldTypes.SELECT,
              props: {
                api: getUnifiedSingleTemplates,
              },
            },
            {
              name: "ticketUnifiedMultipleTemplateId",
              label: "Multi-Finding Template",
              colSpan: 12,
              type: FieldTypes.SELECT,
              props: {
                api: getUnifiedMultiTemplates,
              },
            },
          ]
        : []),
      ...(featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING ? [] : data.projectKey && !_.isEmpty(priorities)
      ? [
          {
            label: `ArmorCode Severity -> ${name} Priority`,
            type: FieldTypes.GROUP,
            fields: [
              {
                name: "critical",
                label: <Vocabulary name="critical"/>,
                ...commonSeverityProps,
              },
              {
                name: "high",
                label: <Vocabulary name="high"/>,
                ...commonSeverityProps,
              },
              {
                name: "medium",
                label: <Vocabulary name="medium"/>,
                ...commonSeverityProps,
              },
              {
                name: "low",
                label: <Vocabulary name="low"/>,
                ...commonSeverityProps,
              },
              {
                name: "info",
                label: <Vocabulary name="info"/>,
                ...commonSeverityProps,
              },
            ],
          },
        ]
      : []),
      ...(featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING ? [] : [
        {
          label: `Notification Level -> ${name} severity`,
          type: FieldTypes.GROUP,
          fields: [
            {
              name: "critical_ticket_severity_mapping",
              label: <Vocabulary name="critical"/>,
              ...commonAzureBoardSeverityProps,
            },
            {
              name: "high_ticket_severity_mapping",
              label: <Vocabulary name="high"/>,
              ...commonAzureBoardSeverityProps,
            },
            {
              name: "medium_ticket_severity_mapping",
              label: <Vocabulary name="medium"/>,
              ...commonAzureBoardSeverityProps,
            },
            {
              name: "low_ticket_severity_mapping",
              label: <Vocabulary name="low"/>,
              ...commonAzureBoardSeverityProps,
            },
            {
              name: "info_ticket_severity_mapping",
              label: <Vocabulary name="info"/>,
              ...commonAzureBoardSeverityProps,
            },
          ],
        }
      ]),
      {
          name: "markResolved",
          label: <> Close Ticket when Finding is <Vocabulary name={"mitigated"} /> <Tooltip title={<>Automatically closes {name} ticket when an issue is fixed. The other states that may trigger ticket close are <ResolvedFindingStatusNames/> based on the global configuration as provided <br/><Typography.Link href="#/globalsettings?activeStep=step17">Click here</Typography.Link></>}  placement="top"> <HelpIcon /> </Tooltip>
          </>,
          type: FieldTypes.BOOLEAN,
          colSpan: 12,
          inline:true,
          props: {
              onChange: handleMarkResolveChecked
          },
          className:"m-t-l"
      },
      {
          name: "resolvedStatus",
          label: "Close Transition Name",
          help: `Close Transition Name should be set as the final (close) status in your defined ${name} workflow for your project. For example 'CLOSED', 'RESOLVED' or 'DONE'.`,
          type: FieldTypes.SELECT,
          options: transitionStatusOptions,
          required: !isResolvedStatusDisabled,
          disabled: isResolvedStatusDisabled,
          colSpan: 12,
      },
      {
        name: "markReopen",
        label: "Re-open ticket if finding remains unresolved from a scan",
        type: FieldTypes.BOOLEAN,
        colSpan: 12,
        inline:true,
        props: {
            onChange: handleMarkReopen
        },
        className:"m-t-l"
      },
      {
          name: "reopenStatus",
          label: "Re-open Transition Name",
          type: FieldTypes.SELECT,
          options: transitionStatusOptions,
          disabled: !markReopen,
          required: markReopen,
          colSpan: 12,
          help: <>{name} status(transition) which can be directly assigned from closed status(transition) once ticket reopens.</>,
          props: {
              allowClear: true
          },
      },
      {
        name: "labels",
        label: "Tags",
        help: `These "tags" will be added to the tickets.`,
        colSpan: 12,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        props: {
          mode: "tags",
          api: fetchJiraLabels,
        },
        rules: [validateBlankSpace("Labels can't contain blank spaces.")],
      }
    ],
    [
      commonSeverityProps,
      data,
      form,
      getIssueTypes,
      getProjectKeys,
      isEdit,
      name,
      priorities,
      productSubProductFields,
      commonAzureBoardSeverityProps,
      transitionStatusOptions,
      handleMarkResolveChecked,
      isResolvedStatusDisabled,
      featureFlags.UNIFIED_TICKET_TEMPLATE,
      getUnifiedSingleTemplates,
      getUnifiedMultiTemplates,
      handleMarkReopen,
      markReopen,
      featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING,
      fetchJiraLabels
    ]
  );

  const onOk = async() => {
    await form.validateFields();
    const toolName = name.toUpperCase().replace(' ', '_');
    const data = form.getFieldsValue();
    data.customFields = [];
    data.customFields = setAzureBoardCustomFields(data);
    
    const configValues = await generateAzureBoardResponse(data, props.config?.id, loginConfigId, url, toolName, thisRef.current?.getCustomFields(), featureFlags);
    if(!_.isPlainObject(configValues)){
        return Promise.reject("Mapping already exist");
    }
    if(!configValues.defaultConfiguration){
      message.warn("Default configuration does not exist, mark one of them as default");
    }
    await ToolsIntegrationsAPI[!isEdit ? "integrateTicketSystemTool" : "updateTicketSystemToolMapping"](configValues, props.config?.id).promise;
    message.success(`${name} Mapping saved`);
    onUpdateConfigComponent && onUpdateConfigComponent(true);
}

const modalProps = {
    title: `${isEdit ? "Edit" : "Add"} ${name} Mapping`,
    width: MODAL_SIZE_LARGE,
    disabled: !hasPermission(isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS) || isCustomFieldsLoading,
    onOk,
  };

  return (
    <BaseModal ref={ref} modalProps={modalProps} afterClose={props.afterClose}>
      <Form
        form={form}
        initialValues={data}
        layout="vertical"
        onValuesChange={(values) => thisRef.current?.onFieldValuesChange?.(values)}
      >
        {generateFormFields(fields)}
      </Form> 
      <RenderCustomFields form={form}
          ref={thisRef}
          initialValues={data}
          loginConfigId={loginConfigId}
          changeMode={isEdit}
          configId={props.config?.id}
          toolName={name}
          onLoadingChange={setIsCustomFieldsLoading}
      />
    </BaseModal>
  );
}

export default forwardRef(AzureBoardMappingForm);

const generateAzureBoardResponse = async (
  data,
  id,
  loginConfigId,
  url,
  toolName,
  customFieldsData,
  featureFlags
) => {
  const severities = Object.keys(SEVERITY_COLOR);
  const configValues = _.omit(data, severities);
  configValues.enabled = true;
  configValues.properties = {};
  severities.forEach((s) => {
    if (data[s]) {
      configValues.properties[s] = data[s];
    }
    //map and add Azure Board Severities inside properties json
    if(!isNil(`${data[s]}_ticket_severity_mapping`)){
      configValues.properties[`${s}_ticket_severity_mapping`] = data[`${s}_ticket_severity_mapping`];
    }
  });
  if (!isUndefined(configValues.transitions)) {
    configValues.transitions = isEmpty(configValues.transitions)
      ? []
      : JSON.parse(configValues.transitions);
  }
  configValues.ticketSystemType = toolName;
  configValues.loginConfigId = loginConfigId;
  if (url) {
    configValues.url = url;
  }
  const isAllProduct = configValues.product[0] === "all";
  const isAllSubproduct = configValues.subProduct[0] === "all";
  if (isAllProduct) {
    delete configValues.product;
  }
  if (isAllSubproduct) {
    delete configValues.subProduct;
  }
  if (isAllProduct && isAllSubproduct) {
    configValues.configurationType = "GLOBAL";
  } else if ((!isAllProduct && isAllSubproduct) || configValues.product.length > 1) {
    configValues.configurationType = "PRODUCT";
  } else {
    configValues.configurationType = "SUBPRODUCT";
  }

  if (customFieldsData) {
    const { inputCustomFields, customFieldState: fieldsChecked } =
      customFieldsData;
    configValues.customFields = [];
    _.forEach(inputCustomFields, (cstmField) => {
      let fieldValue = isArray(data[cstmField.key])
        ? data[cstmField.key].join()
        : data[cstmField.key];

      // cascadingselect: value format `parent:child`
      if (
        cstmField.type === "cascadingselect" &&
        _.isArray(data[cstmField.key])
      ) {
        fieldValue = `${data[cstmField.key][0]}:${data[cstmField.key][1]}`;
      }
      
      //setup sevrity/priority group field payload
      if(featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING) {
        const mappings = getSeverityFieldMappingsPayload(cstmField, data);
        if(!isEmpty(mappings)){
            cstmField.mappings = mappings;
        }
      }

      // if fieldValue is mismatch with defaultValue means defaultValue is from template
      const field = {
        ...cstmField,
        active: !!fieldsChecked[cstmField.key] || cstmField.isRequired,
        defaultVal: fieldValue,
        value: fieldValue,
      };
      if((CUSTOM_FIELDS_WITH_SEARCH.includes(cstmField.type) || CUSTOM_FIELDS_WITH_USER_API.includes(cstmField.type)) && (!isEmpty(data[cstmField.key]) && !isString(data[cstmField.key]))){
        const {fieldValue: value, selectedValues} = getCustomFieldSelectedValuesPayload(data, cstmField);
        field.defaultVal = value;
        field.selectedValues = selectedValues;
        field.value = value;
        fieldValue = value;
      }
      if (_.isBoolean(fieldValue) || !_.isEmpty(fieldValue)) {
        configValues[cstmField.key] = fieldValue;
        configValues.customFields.push(field);
      }
    });
  }

  return configValues;
};

export const parseAreaOptions = (treeData)=>{
  let treeDataClone = [];
  treeDataClone = treeData.map((dt)=>{
    const data = {title: dt?.name, value: dt?.id};
    if(!isEmpty(dt?.allowedValues)){
      data.children = parseAreaOptions(dt.allowedValues);
    }
    return data;
  }).filter(dt => dt.title);
  return  treeDataClone;
}
